import React, { useState, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import Section, { ExpandedContent, SectionHeader, ViewMoreButton } from './Section'
import Reviews from './Reviews'
import pluralize from 'pluralize'
import { api } from 'components/api'
import { AuthContext, isSupplierOwner, showPublicOnly, USER_TYPES } from 'components/common/auth/AuthContext'
import SupplierShape from './prop_shapes/supplier'
import ReviewMetadataShape from './prop_shapes/reviewMetadata'
import { supplierReviewsPath } from 'components/routes/api'
import ReviewCreateButton from './ReviewCreateButton'
import { ReviewVisibilityCallout } from 'components/common/callout'
import ReviewRepliesCount from 'components/supplier_show/ReviewRepliesCount'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ReviewFiltersContainer } from 'components/review_tiles/filters/ReviewFiltersContainer'

export function reviewRequestData (supplier) {
  return {
    address: 'reviews@procurated.com',
    subject: 'Requesting Supplier Reviews on Procurated',
    body: 'I am requesting additional reviews from members of the procurement community for this supplier: ' +
      supplier.procuratedUrl
  }
}

export const baseReviewFetcher = async (url, lastReviewId) => {
  const response = await api.get({ url, params: { last_review_id: lastReviewId } })
  return response.data
}

export function ReviewSection (props) {
  const reviewFetcher = props.reviewFetcher ?? baseReviewFetcher
  const { metadata, supplier, procuratedPromisePath } = props
  const authContext = useContext(AuthContext)

  const [reviewFilterParams, setReviewFilterParams] = useState('')
  // pagesState is used to hold the last response data while updating to the new response
  // this avoids the response data being empty and rendering an empty list while updating
  const pagesState = useRef([])

  const showQualifiedAutopubReviews = () => {
    // This continues a pre-existing hack where a boolean is returned true only if there is no reply or shared query
    const paramObj = reviewFilterParams || {}
    const noReplyParams = !('replied_to' in (paramObj))
    const noSharedParams = !('shared' in (paramObj))

    return noReplyParams && noSharedParams
  }

  const getLastReviewId = (lastPage) => lastPage?.reviews[lastPage.reviews.length - 1]?.id

  const handleReviewFiltersQuery = (params) => {
    setReviewFilterParams(params)
  }
  const reviewFilters = <ReviewFiltersContainer handleFiltersQuery={handleReviewFiltersQuery} />

  const reviewFilterQuery = new URLSearchParams(reviewFilterParams).toString()
  const supplierReviewsPathWithFilters = () => `${supplierReviewsPath(supplier.id)}?${reviewFilterQuery}`

  // Query for paginated reviews
  const fetchReviews = ({ pageParam: lastReviewId }) => {
    return reviewFetcher(supplierReviewsPathWithFilters(), lastReviewId)
  }

  const { data, fetchNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['newReviews', reviewFilterParams],
    queryFn: fetchReviews,
    getNextPageParam: getLastReviewId
  })

  const pages = data?.pages ?? pagesState.current
  pagesState.current = pages
  const fetchedReviews = pages.filter(Boolean).map(x => x?.reviews).flat()
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : {}
  const hasNextPage = lastPage?.has_next_page
  const showLoadMoreButton = hasNextPage && !isLoading

  const fetchedAutoPubReviews = () => {
    if (showQualifiedAutopubReviews() && !hasNextPage) {
      return pages.find(x => x?.auto_pub_condensed_reviews)
    }
  }

  // Render elements
  function callout () {
    const baseString = `${metadata.review_count} ${pluralize('REVIEW', metadata.review_count)}`
    if (authContext.isOwner) {
      return `${baseString}, ${metadata.visible_review_count} VISIBLE`
    } else {
      return baseString
    }
  }

  function renderRightCallout () {
    if (authContext.userType === USER_TYPES.AGENCY_USER) {
      const uriData = reviewRequestData(supplier)
      const supplierPageURI = `mailto:${uriData.address}?` +
        `subject=${encodeURIComponent(uriData.subject)}&body=${encodeURIComponent(uriData.body)}`
      return (
        <>
          <a
            className='button pro-orange-400-outline margin-right-05 supplier-profile-button'
            href={supplierPageURI}
          >
            Request Review
          </a>
          {writeAReviewButton()}
        </>
      )
    } else if (authContext.userType === USER_TYPES.UNAUTHED) {
      return writeAReviewButton()
    } else {
      return (
        <>
          <ReviewVisibilityCallout proSupplier={supplier.pro} slug={supplier.slug} />
        </>
      )
    }
  }

  function writeAReviewButton () {
    return (
      <ReviewCreateButton
        supplierId={supplier.id}
        text={<>Write a Review <i className='fas fa-edit' /></>}
      />
    )
  }

  const reviewRepliesCount = metadata?.review_replies_count || 0
  const renderReviewRepliesCount = () => <ReviewRepliesCount count={reviewRepliesCount} />

  return (
    <Section
      expandable
      expanded
      idText='reviews'
      locked={showPublicOnly(authContext)}
    >
      <SectionHeader
        callout={callout()}
        secondaryCallout={renderReviewRepliesCount()}
        rightCallout={<div className='hide-for-small-only'>{renderRightCallout()}</div>}
        idText='reviews'
        multiline={isSupplierOwner(authContext)}
        {...props}
      >
        Reviews & Ratings
      </SectionHeader>

      <ExpandedContent>
        <Reviews
          callout={callout()}
          metadata={metadata}
          reviews={fetchedReviews}
          autoPubCondensedReview={fetchedAutoPubReviews()}
          procuratedPromisePath={procuratedPromisePath}
          reviewButtons={renderRightCallout()}
          supplier={supplier}
          reviewFilters={reviewFilters}
        />
      </ExpandedContent>

      <ViewMoreButton show={showLoadMoreButton} onViewMore={fetchNextPage} />
    </Section>
  )
}

ReviewSection.propTypes = {
  supplier: SupplierShape.isRequired,
  metadata: ReviewMetadataShape.isRequired,
  procuratedPromisePath: PropTypes.string.isRequired,
  reviewFetcher: PropTypes.func
}

export default ReviewSection
